import React, { useState, useEffect } from "react";
import styles from "./BodyMap.module.css";
import ColorDropdown from "../../LayoutComponents/ColorDropdown";

function BodyMap({ imageUrl, pins = [], lockParent, saveBodyMap }) {
  const [hoveredPinId, setHoveredPinId] = useState(null);
  const [stickyPinId, setStickyPinId] = useState(null);
  const [confirmedPins, setConfirmedPins] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [initialPins, setInitialPins] = useState(pins);
  const [newPin, setNewPin] = useState({
    x: null,
    y: null,
    color: "#FFFFFF",
    size: "medium",
    note: "",
  });

  const PROXIMITY_TOLERANCE = 0.55;
  const pinRadiusBySize = {
    small: 0.01,
    medium: 0.015,
    large: 0.025,
  };

  useEffect(() => {
    setNewPin({
      x: null,
      y: null,
      color: "#FFFFFF",
      size: "medium",
      note: "",
    });
  }, [imageUrl]);

  useEffect(() => {
    const pinsChanged =
      pins.length !== initialPins.length || pins.some((p, i) => JSON.stringify(p) !== JSON.stringify(initialPins[i]));

    if (pinsChanged) {
      setInitialPins(pins);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins]);

  function getTooltipPositionClass(pin) {
    const { x, y } = pin;
    const edgeThreshold = 0.12;

    if (y < edgeThreshold) return styles.tooltipBelow;
    if (y > 0.88) return styles.tooltipAbove;
    if (x < 0.15) return styles.tooltipRight;
    if (x > 0.85) return styles.tooltipLeft;
    return styles.tooltipAbove;
  }

  const generateTempId = () => {
    const now = new Date();
    return -(
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0") +
      String(now.getMilliseconds()).padStart(3, "0")
    );
  };

  function getpinSize(size) {
    switch (size) {
      case 1:
        return "small";
      case 2:
        return "medium";
      case 3:
        return "large";
      default:
        return "medium";
    }
  }

  const isPinTooClose = (x, y, size) => {
    const newRadius = pinRadiusBySize[size] * PROXIMITY_TOLERANCE;
    const allPins = [...initialPins, ...confirmedPins];

    return allPins.some((pin) => {
      const existingRadius = pinRadiusBySize[getpinSize(pin.size)] * PROXIMITY_TOLERANCE;
      const dx = pin.x - x;
      const dy = pin.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < newRadius + existingRadius;
    });
  };

  const handleConfirmPin = () => {
    lockParent(true);
    const sizeMap = { small: 1, medium: 2, large: 3 };
    setConfirmedPins([...confirmedPins, { ...newPin, id: generateTempId(), size: sizeMap[newPin.size] }]);
    setNewPin({
      x: null,
      y: null,
      color: "#FFFFFF",
      size: "medium",
      note: "",
    });
  };

  const handleDeletePin = (pinToDelete) => {
    if (confirmedPins.some((p) => p.id === pinToDelete.id)) {
      setConfirmedPins(confirmedPins.filter((p) => p.id !== pinToDelete.id));
    } else if (initialPins.some((p) => p.id === pinToDelete.id)) {
      setIsDirty(true);
      setInitialPins(initialPins.filter((p) => p.id !== pinToDelete.id));
    }
  };

  const handleSave = async () => {
    const pins = [...initialPins, ...confirmedPins];
    await saveBodyMap(pins);
    setConfirmedPins([]);
    lockParent(false);
  };

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <ColorDropdown value={newPin.color} onChange={(val) => setNewPin({ ...newPin, color: val })} />

          <div className={styles.sizePicker}>
            {["small", "medium", "large"].map((size) => (
              <label key={size} className={styles.sizeOption}>
                <input
                  type="radio"
                  name="pinSize"
                  value={size}
                  checked={newPin.size === size}
                  onChange={() => setNewPin({ ...newPin, size })}
                />
                <span className={`${styles.sizeCircle} ${styles[`size_${size}`]}`} />
              </label>
            ))}
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <button
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={() => {
              lockParent(false);
              setIsDirty(false);
              setConfirmedPins([]);
              setNewPin({
                x: null,
                y: null,
                color: "#137C8B",
                size: "medium",
                note: "",
              });
            }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={!isDirty && confirmedPins.length === 0} className={styles.button}>
            Sauvegarder
          </button>
        </div>
      </div>
      <div
        className={styles.bodyMapContainer}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;

          if (!isPinTooClose(x, y, newPin.size)) {
            setStickyPinId(null);
            setNewPin({ ...newPin, x, y });
          }
        }}>
        {newPin.x !== null && (
          <div
            className={`${styles.pin} ${styles[`pinSize_${newPin.size}`]} ${styles.noHoverEffect}`}
            style={{
              left: `${newPin.x * 100}%`,
              top: `${newPin.y * 100}%`,
              backgroundColor: newPin.color,
            }}>
            <div className={`${styles.noteEditorWrapper} ${getTooltipPositionClass(newPin)}`}>
              <div className={styles.pinActions}>
                <button
                  className={styles.confirmPinButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmPin();
                  }}>
                  ✔
                </button>
                <button
                  className={styles.cancelPinButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewPin({
                      x: null,
                      y: null,
                      color: "#FFFFFF",
                      size: "medium",
                      note: "",
                    });
                    lockParent(false);
                  }}>
                  ✕
                </button>
              </div>

              <textarea
                className={styles.pinNoteInput}
                value={newPin.note}
                onChange={(e) => setNewPin({ ...newPin, note: e.target.value })}
                placeholder="Note..."
                onClick={(e) => e.stopPropagation()}
                rows={2}
              />
            </div>
          </div>
        )}
        <img src={imageUrl} alt="Body map" className={styles.image} />
        <div className={styles.pinsLayer}>
          {[...initialPins, ...confirmedPins].map((pin) => (
            <div
              key={pin.id}
              className={`${styles.pin} ${styles[`pinSize_${getpinSize(pin.size)}`]}`}
              style={{
                left: `${pin.x * 100}%`,
                top: `${pin.y * 100}%`,
                backgroundColor: pin.color || "#000",
              }}
              onClick={() => setStickyPinId((prev) => (prev === pin.id ? null : pin.id))}
              onMouseEnter={() => setHoveredPinId(pin.id)}
              onMouseLeave={() => setHoveredPinId(null)}>
              {(hoveredPinId === pin.id || stickyPinId === pin.id) && pin.note && pin.note.trim() !== "" && (
                <div className={`${styles.tooltip} ${getTooltipPositionClass(pin)}`}>{pin.note}</div>
              )}

              {stickyPinId === pin.id && (
                <button
                  className={styles.deletePinButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePin(pin);
                  }}>
                  X
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default BodyMap;
